import PwaNetworkStatusController from "./pwa/network_status_controller"
import { logEvent } from "../field_logger"
import { logSpeedEvent, CONNECTIVITY_ENTERED_DEGRADED, CONNECTIVITY_ENTERED_OFFLINE } from "../lib/field_speed_events"
import { recordSample, LEVEL_OFFLINE, LEVEL_DEGRADED, LEVEL_ONLINE } from "../field_network_health"
import { offlineFirstPlaceIds, clearAllOnRecovery } from "../field_place_connectivity"

// Field app network status -- thin wrapper over the shared PWA controller.
// Adds: prefix "field", service worker sync, field_logger integration,
// connectivity-level evidence recording (ADR-0110, Card 1), and bridging
// Degraded/Offline transitions into the app_log stream (ADR-0110, Card 4) so
// the server can auto-set Place#bad_connectivity from real telemetry.
//
// field_network_health.js itself stays a pure leaf module with no
// side-effecting imports -- this controller is the field-specific bridge
// that turns its events into a logged fact, not the health module itself.

const LEVEL_EVENT = {
  [LEVEL_OFFLINE]:  CONNECTIVITY_ENTERED_OFFLINE,
  [LEVEL_DEGRADED]: CONNECTIVITY_ENTERED_DEGRADED
}

export default class extends PwaNetworkStatusController {
  static values = {
    ...PwaNetworkStatusController.values,
    prefix:        { type: String, default: "field" },
    swSyncEnabled: { type: Boolean, default: true }
  }

  connect() {
    super.connect()
    this._healthChangedHandler = (e) => this._onHealthChanged(e.detail)
    window.addEventListener("field:health-changed", this._healthChangedHandler)
    // Every field page re-syncs the service worker's offline-first-place set
    // on load (card #4310) -- covers a restarted SW, and a page (e.g. Today)
    // that never mounts the place-scoped tracking controller itself.
    this._postOfflineFirstState()
  }

  disconnect() {
    window.removeEventListener("field:health-changed", this._healthChangedHandler)
    super.disconnect()
  }

  _log(message, detail) {
    logEvent("network", message, detail).catch(() => {})
  }

  // Feeds the ping poll's duration/outcome into field_network_health's
  // evidence window -- zero change to this controller's own res.ok pass/fail
  // logic or field:online semantics (that stays entirely in the base class).
  _recordPingSample(durationMs, outcome) {
    recordSample({ kind: "ping", durationMs, outcome, sizeClass: "small" })
  }

  // Reports a transition INTO Degraded or Offline as a connectivity app_log
  // entry -- the server (Api::V1::Field::LogsController) uses this to
  // auto-set Place#bad_connectivity so the *next* visit starts pre-warmed.
  // Transitions into Online are not logged -- this is about detecting bad
  // connectivity, not recording every recovery. place_id (if any) comes
  // from field_logger's ambient setLogContext(), not passed explicitly here.
  _onHealthChanged({ level, previousLevel }) {
    const eventName = LEVEL_EVENT[level]
    if (eventName) {
      logSpeedEvent(eventName, { level, previousLevel }).catch(() => {})
    }

    // A real, sustained recovery (field_network_health.js's own hysteresis --
    // >=30s dwell + 2 good samples) clears every place's threshold-earned
    // offline-first latch (card #4310, "until signal recovers"). This runs
    // here, not in the place-scoped controller, because recovery is a
    // session-wide fact, not a per-place one.
    if (level === LEVEL_ONLINE) {
      clearAllOnRecovery()
      this._postOfflineFirstState()
    }
  }

  _postOfflineFirstState() {
    const placeIds = offlineFirstPlaceIds()
    navigator.serviceWorker?.controller?.postMessage({ type: "OFFLINE_FIRST_PLACES", placeIds })
    window.dispatchEvent(new CustomEvent("field:offline-first-changed", { detail: { placeIds } }))
  }
}
